import { Box, Checkbox, MenuItem, Typography } from '@material-ui/core';
import {
  getLendersByMerchantId,
  Lender,
  patchAddMerchantIdToLender,
  patchRemoveMerchantIdFromLender,
} from '@nemuru-eng/js-api-client';
import { Dispatch, SetStateAction, useEffect, useState } from 'react';

import { LenderIcon } from '@/components/shared/components/Logo/LenderLogo/LenderIcon';
import { Font } from '@/components/shared/components/Font';
import { NemuruDialog } from '@/components/shared/components/NemuruDialog';
import { StandardButton } from '@/components/shared/components/StandardButton';
import { Text } from '@/components/shared/components/Text';
import { ALL_LENDERS } from '@/constants/AllLenders';
import { useCache } from '@/hooks/requests/useCache';
import { equalArrays } from '@/utils/equalArrays';

interface MerchantInLenderContextDialogProps {
  opened: boolean;
  setOpened: Dispatch<SetStateAction<boolean>>;
  merchantId: string;
  toggleDialog: () => void;
  lenders?: Lender[];
}

export const MerchantInLenderContextDialog = ({
  opened,
  setOpened,
  toggleDialog,
  lenders = [],
  merchantId,
}: MerchantInLenderContextDialogProps) => {
  const { revalidate } = useCache(getLendersByMerchantId, { merchantId });

  const [selectedLenderIds, setSelectedLenderIds] = useState<string[]>([]);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  const lenderIds = lenders?.map((lender) => lender.id);

  useEffect(() => {
    if (lenders?.length) setSelectedLenderIds(lenders.map((lender) => lender.id));
  }, [lenders]);

  const disableSubmit = equalArrays(lenderIds, selectedLenderIds);

  const onSubmit = async (): Promise<void> => {
    setIsSubmitting(true);
    try {
      for await (const selectedLenderId of selectedLenderIds) {
        if (!lenderIds.includes(selectedLenderId)) {
          await patchAddMerchantIdToLender({ id: selectedLenderId, merchantId });
        }
      }
      for await (const lenderId of lenderIds) {
        if (!selectedLenderIds.includes(lenderId)) {
          await patchRemoveMerchantIdFromLender({ id: lenderId, merchantId });
        }
      }
    } catch (err) {
      setIsSubmitting(false);
    }
    setIsSubmitting(false);
    toggleDialog();
    revalidate();
  };

  return (
    <NemuruDialog
      opened={opened}
      setOpened={setOpened}
      buttons={
        <>
          <StandardButton main onClick={onSubmit} disabled={disableSubmit} loading={isSubmitting}>
            <Text tId="SAVE" />
          </StandardButton>
        </>
      }
    >
      <Box mb={4}>
        <Font variant="h3" color="title">
          <Text tId={'EDIT_MERCHANT_IN_LENDER_CONTEXT'} />
        </Font>
      </Box>
      <Box mb={4}>
        <Font variant="body2" color="textSecondary">
          <Text tId={'EDIT_MERCHANT_IN_LENDER_CONTEXT_TEXT'} />
        </Font>
      </Box>
      <Box>
        {ALL_LENDERS.map((lender) => {
          const checked = selectedLenderIds.includes(lender.id);
          const onClick = () => {
            let lenderIds = [...selectedLenderIds, lender.id];
            if (checked) lenderIds = [...selectedLenderIds].filter((lenderId) => lenderId !== lender.id);
            setSelectedLenderIds(lenderIds);
          };
          return (
            <MenuItem key={lender.id} onClick={onClick} style={{ display: 'flex', justifyContent: 'space-between' }}>
              <Box display={'flex'}>
                <LenderIcon
                  lenderName={lender.name}
                  style={{
                    maxHeight: 24,
                    maxWidth: 24,
                    margin: '0px 16px 0px 0px',
                    filter: checked ? 'none' : 'grayscale(100%) brightness(65%) contrast(150%)',
                    opacity: checked ? 1 : 0.5,
                  }}
                />
                <Typography color={checked ? 'textPrimary' : 'textSecondary'}>
                  <Text tId={lender.name} />
                </Typography>
              </Box>
              <Checkbox checked={checked} />
            </MenuItem>
          );
        })}
      </Box>
    </NemuruDialog>
  );
};
